import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, timer } from 'rxjs';
import { AuthService } from '../services/auth.service';

const AUTH_STATE_TIMEOUT_MS = 2000;

/**
 * Authentication Guard
 *
 * Protects routes that require authentication. If user is not authenticated,
 * redirects to login page with return URL.
 *
 * Properly handles page refresh by waiting for auth state to be restored.
 */
export const authGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return new Observable<boolean | UrlTree>((observer) => {
    let resolved = false;

    // First check if already authenticated (immediate check)
    if (authService.isAuthenticated) {
      resolved = true;
      observer.next(true);
      observer.complete();
      return;
    }

    // Subscribe to auth state changes to wait for authentication to be restored
    const subscription = authService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (!resolved && isAuthenticated) {
        resolved = true;
        observer.next(true);
        observer.complete();
      }
    });

    // Give reasonable time for auth state to be restored (especially on page refresh)
    // If not authenticated after this time, redirect to login
    timer(AUTH_STATE_TIMEOUT_MS).subscribe(() => {
      if (!resolved) {
        resolved = true;
        subscription.unsubscribe();

        // Final check before redirecting
        if (authService.isAuthenticated) {
          observer.next(true);
        } else {
          observer.next(router.parseUrl('/login?returnUrl=' + encodeURIComponent(state.url)));
        }
        observer.complete();
      }
    }); // 2 seconds should be enough for MSAL initialization

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  });
};